const COMPARISON_METHOD = {
  id: 'frontend_exact_match',
  label: 'Exact content match',
  description: 'Compares the editable test-case fields against the editor baseline.',
};

const TRACKED_FIELDS = [
  'test_description',
  'test_steps',
  'expected_result',
];

const FIELD_ALIASES = {
  test_description: ['test_description', 'description'],
  test_steps: ['test_steps', 'manual_steps', 'steps'],
  expected_result: ['expected_result', 'expected_results', 'expected'],
};

const LIST_PREFIX_PATTERN = /^\s*(?:(?:[-*])|(?:step\s*)?\d+[).:-]|(?:[a-z][).:-]))\s*/i;
const LIST_BOUNDARY_PATTERN = /\s+(?=(?:step\s*)?\d+[).:-]\s+)/gi;
const LIST_COMPARISON_FIELDS = new Set(['test_steps', 'expected_result']);
const OBJECT_TEXT_KEYS = {
  test_description: ['test_description', 'description', 'title', 'name', 'text', 'value'],
  test_steps: ['step', 'text', 'description', 'instruction', 'action', 'target', 'label', 'value'],
  expected_result: ['expected_result', 'expected_results', 'expected', 'result', 'text', 'description', 'value'],
};

const normalizeText = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

const normalizeComparableText = (value) => normalizeText(value)
  .replace(/\s+([,.;:!?])/g, '$1')
  .replace(/[.;:]+$/g, '')
  .toLowerCase();

const stripListPrefix = (value) => normalizeText(value).replace(LIST_PREFIX_PATTERN, '');

const normalizeForCompare = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return normalizeComparableText(value);
  if (Array.isArray(value)) return value.map((item) => normalizeForCompare(item));
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
        .map(([key, item]) => [key, normalizeForCompare(item)])
    );
  }
  return value;
};

const stableJsonString = (value) => {
  const normalized = normalizeForCompare(value);
  if (typeof normalized === 'string') return normalized;
  try {
    return JSON.stringify(normalized);
  } catch (_err) {
    return String(normalized ?? '');
  }
};

const splitTextItems = (value) => {
  const text = String(value ?? '')
    .replace(/\r\n?/g, '\n')
    .replace(LIST_BOUNDARY_PATTERN, '\n')
    .trim();
  if (!text) return [];
  return text
    .split(/\n+/)
    .map(stripListPrefix)
    .map(normalizeComparableText)
    .filter(Boolean);
};

const toComparableItems = (value, field, seen = new Set()) => {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string') return splitTextItems(value);
  if (Array.isArray(value)) {
    return value.flatMap((item) => toComparableItems(item, field, seen));
  }
  if (typeof value === 'object') {
    if (seen.has(value)) return [];
    seen.add(value);
    const keys = OBJECT_TEXT_KEYS[field] || [];
    const chunks = [];
    keys.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(value, key)) return;
      chunks.push(...toComparableItems(value[key], field, seen));
    });
    if (chunks.length > 0) {
      return field === 'test_steps'
        ? [normalizeComparableText(chunks.join(' '))]
        : chunks;
    }
    return [stableJsonString(value)];
  }
  return [normalizeComparableText(value)];
};

const toCompareString = (value, field) => {
  if (LIST_COMPARISON_FIELDS.has(field)) {
    return toComparableItems(value, field).join('\n');
  }
  if (field === 'test_description') {
    const items = toComparableItems(value, field);
    if (items.length > 0) return items.join(' ');
  }
  return stableJsonString(value);
};

const toDisplayString = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return normalizeText(value);
  if (Array.isArray(value)) {
    if (value.every((item) => item === null || typeof item !== 'object')) {
      return value.map((item) => `- ${normalizeText(item)}`).join('\n');
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (_err) {
    return String(value ?? '');
  }
};

const normalizeCaseGroup = (testCases) => {
  if (Array.isArray(testCases)) {
    return {
      positive: testCases.map((testCase) => ({ ...testCase, _category: testCase?._category || 'positive' })),
      negative: [],
    };
  }
  if (!testCases || typeof testCases !== 'object') {
    return { positive: [], negative: [] };
  }
  return {
    positive: (Array.isArray(testCases.positive) ? testCases.positive : Object.values(testCases.positive || {}))
      .map((testCase) => ({ ...testCase, _category: 'positive' })),
    negative: (Array.isArray(testCases.negative) ? testCases.negative : Object.values(testCases.negative || {}))
      .map((testCase) => ({ ...testCase, _category: 'negative' })),
  };
};

const isManualUpload = (testCase) => (
  String(testCase?.source_type || testCase?._source || '').trim().toLowerCase() === 'manual_upload' ||
  String(testCase?._source || '').trim().toLowerCase() === 'manual'
);

const getCaseId = (testCase) => String(
  testCase?.test_case_no ||
  testCase?.test_case_id ||
  testCase?.id ||
  ''
).trim();

const resolveFieldValue = (testCase, field) => {
  const aliases = FIELD_ALIASES[field] || [field];
  for (const key of aliases) {
    if (Object.prototype.hasOwnProperty.call(testCase || {}, key)) {
      return testCase[key];
    }
  }
  return '';
};

const compareField = (field, originalValue, modifiedValue) => {
  const original = toCompareString(originalValue, field);
  const modified = toCompareString(modifiedValue, field);
  const changed = original !== modified;
  return {
    original: toDisplayString(originalValue),
    modified: toDisplayString(modifiedValue),
    changed,
    length_original: original.length,
    length_modified: modified.length,
    length_change: modified.length - original.length,
    similarity: {
      score: changed ? 0 : 100,
      method: COMPARISON_METHOD.id,
      label: COMPARISON_METHOD.label,
    },
  };
};

const pairCases = (baselineCases, currentCases) => {
  const currentById = new Map();
  currentCases.forEach((testCase, index) => {
    const caseId = getCaseId(testCase);
    if (caseId && !currentById.has(caseId)) {
      currentById.set(caseId, { testCase, index });
    }
  });

  const usedCurrentIndexes = new Set();
  const pairs = [];

  baselineCases.forEach((baselineCase, index) => {
    const caseId = getCaseId(baselineCase);
    const currentEntry = caseId ? currentById.get(caseId) : null;
    if (currentEntry && !usedCurrentIndexes.has(currentEntry.index)) {
      pairs.push([baselineCase, currentEntry.testCase]);
      usedCurrentIndexes.add(currentEntry.index);
      return;
    }

    const positionalCurrent = currentCases[index];
    if (positionalCurrent && !usedCurrentIndexes.has(index)) {
      pairs.push([baselineCase, positionalCurrent]);
      usedCurrentIndexes.add(index);
      return;
    }

    pairs.push([baselineCase, null]);
  });

  currentCases.forEach((currentCase, index) => {
    if (!usedCurrentIndexes.has(index)) {
      pairs.push([null, currentCase]);
    }
  });

  return pairs;
};

const compareCase = (baselineCase, currentCase) => {
  const fields = {};
  TRACKED_FIELDS.forEach((field) => {
    fields[field] = compareField(
      field,
      resolveFieldValue(baselineCase, field),
      resolveFieldValue(currentCase, field)
    );
  });

  const changedFields = Object.entries(fields)
    .filter(([, field]) => field.changed)
    .map(([fieldName]) => fieldName);

  return {
    test_case_id: getCaseId(currentCase) || getCaseId(baselineCase) || null,
    test_case_no: getCaseId(currentCase) || getCaseId(baselineCase) || null,
    category: currentCase?._category || baselineCase?._category || null,
    test_description: normalizeText(
      resolveFieldValue(currentCase, 'test_description') ||
      resolveFieldValue(baselineCase, 'test_description')
    ),
    tracked_fields: TRACKED_FIELDS,
    tracked_fields_count: TRACKED_FIELDS.length,
    fields,
    overall_similarity: changedFields.length > 0 ? 0 : 100,
    changed_fields_count: changedFields.length,
    changed_fields: changedFields,
    unchanged_fields_count: TRACKED_FIELDS.length - changedFields.length,
    has_changes: changedFields.length > 0,
  };
};

export const buildTestCaseComparisonMetrics = (currentTestCases, baselineTestCases) => {
  const currentGroup = normalizeCaseGroup(currentTestCases);
  const baselineGroup = normalizeCaseGroup(baselineTestCases);
  const comparisons = [];
  let currentAiCasesCount = 0;
  let baselineAiCasesCount = 0;

  ['positive', 'negative'].forEach((category) => {
    const currentCases = currentGroup[category].filter((testCase) => !isManualUpload(testCase));
    const baselineCases = baselineGroup[category].filter((testCase) => !isManualUpload(testCase));
    currentAiCasesCount += currentCases.length;
    baselineAiCasesCount += baselineCases.length;
    pairCases(baselineCases, currentCases).forEach(([baselineCase, currentCase]) => {
      comparisons.push(compareCase(baselineCase, currentCase));
    });
  });

  if (comparisons.length === 0 || baselineAiCasesCount === 0 || currentAiCasesCount === 0) return null;

  const changedCount = comparisons.filter((comparison) => comparison.has_changes).length;
  const averageSimilarity = comparisons.length > 0
    ? comparisons.reduce((sum, comparison) => sum + comparison.overall_similarity, 0) / comparisons.length
    : 0;
  const overall = {
    test_cases_count_1: comparisons.length,
    test_cases_count_2: comparisons.length,
    comparisons,
    overall_average_similarity: Number(averageSimilarity.toFixed(2)),
    comparison_method: COMPARISON_METHOD,
    statistics: {
      total_test_cases_compared: comparisons.length,
      average_similarity: Number(averageSimilarity.toFixed(2)),
      minimum_similarity: Math.min(...comparisons.map((comparison) => comparison.overall_similarity)),
      maximum_similarity: Math.max(...comparisons.map((comparison) => comparison.overall_similarity)),
      test_cases_with_changes: changedCount,
      test_cases_unchanged: comparisons.length - changedCount,
      changed_fields_total: comparisons.reduce(
        (sum, comparison) => sum + comparison.changed_fields_count,
        0
      ),
    },
  };

  return {
    comparison_scope: 'initial_ai_baseline',
    comparison_method: COMPARISON_METHOD,
    baseline: {
      origin: 'editor_baseline',
      ai_cases_count: baselineAiCasesCount,
      current_ai_cases_count: currentAiCasesCount,
      current_total_cases_count: currentGroup.positive.length + currentGroup.negative.length,
    },
    overall,
    ai_generated: overall,
    manual_uploaded: {
      status: 'tracked_separately',
      reason: 'Manual uploads are excluded from the initial AI comparison.',
      added_count: currentGroup.positive.concat(currentGroup.negative).filter(isManualUpload).length,
    },
  };
};
